class ShopfloTheme {
  constructor(config) {
    this.config = config || window.shopfloThemeConfig || {};
    this.buyNowButton = document.getElementById('flo-buy-now-button');
    this.nativeBuyNowWrapper = document.getElementById('shopify-buy-now__button--wrapper');

    this.init();
  }

  init() {
    // Master on/off (window.shopfloThemeConfig.shopflo_enable, hardcoded in
    // snippets/shopflo.liquid - NOT a Theme Editor setting) is deliberately NOT checked here.
    // The checkout/buy-now/cart markup always renders the same way regardless, so every binding
    // below must stay active either way - shouldTriggerFloDirectly() is what actually decides,
    // at click time, whether to open Shopflo's real flow or fall back to a plain navigation.
    this.syncCartAutoOpenGlobal();
    this.bindGlobalTriggers();
    this.bindBuyNowAtcSync();
    this.bindBuyNowIntlFallback();
    this.bindDomEvents();
    this.bindCartIconTriggers();
    this.bindPopupMorph();
  }

  isDomesticTimezone() {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone === 'Asia/Calcutta' || timeZone === 'Asia/Kolkata';
  }

  // The single click-time decision point: should this interaction go through Shopflo's real
  // bundle, or fall back to a plain native action? False when Shopflo is disabled entirely
  // (this.config.shopflo_enable), OR when the shopper should be internationally redirected
  // instead (shopflo_international_redirect_enabled and not on a domestic timezone).
  shouldTriggerFloDirectly() {
    if (this.config.shopflo_enable === false) return false;
    return !this.config.shopflo_international_redirect_enabled || this.isDomesticTimezone();
  }

  // cart-drawer.js / cart-notification.js read this global directly to decide whether to auto-open after add-to-cart
  syncCartAutoOpenGlobal() {
    window.shopfloCartAutoOpen = this.config.shopflo_cart_open_after_atc !== false;
  }

  // Named openThemeFloCheckout (not openFloCheckout) deliberately - same reasoning as
  // openThemeFloCart below: the Shopflo bundle script defines its own globals
  // (handleFloCheckoutBtn, etc.); prefixing ours with "Theme" avoids silently colliding with
  // or overriding a same-named function the bundle might also expose.
  openThemeFloCheckout() {
    if (this.shouldTriggerFloDirectly()) {
      if (typeof window.handleFloCheckoutBtn === 'function') window.handleFloCheckoutBtn();
    } else {
      window.location.href = '/checkout';
    }
  }

  // Named openThemeFloCart (not openFloCart) deliberately - the Shopflo bundle script defines
  // its own globals (handleFloCartBtn, etc.); prefixing ours with "Theme" avoids silently
  // colliding with or overriding a same-named function the bundle might also expose.
  openThemeFloCart() {
    if (this.shouldTriggerFloDirectly()) {
      // Always a plain, foreground open - `loadCartInBackground: true` tells the bundle to load
      // WITHOUT showing anything, so passing it here would silently do nothing on click. Any
      // background warming for "Cart Click" mode happens earlier, at add-to-cart time - see
      // bindDomEvents().
      if (typeof window.handleFloCartBtn === 'function') window.handleFloCartBtn();
    } else {
      window.location.href = '/cart';
    }
  }

  // snippets/shopflo.liquid renders onclick="openThemeFloCheckout()" as a plain global call
  bindGlobalTriggers() {
    window.openThemeFloCheckout = this.openThemeFloCheckout.bind(this);
    window.openThemeFloCart = this.openThemeFloCart.bind(this);
  }

  // keeps the Buy Now button's disabled state in sync with the product form's Add to Cart
  // button as the shopper switches variants, on top of the initial server-rendered state
  bindBuyNowAtcSync() {
    if (!this.buyNowButton) return;

    const scope =
      this.buyNowButton.closest('form[action*="/cart/add"]') ||
      this.buyNowButton.closest('product-info, .product, [data-section-type="product"], section') ||
      document;
    const atcButton =
      scope.querySelector('button[name="add"], [name="add"]:not([type="hidden"])') ||
      document.querySelector(
        'form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] button[type="submit"]'
      );
    if (!atcButton) return;

    const sync = () => {
      this.buyNowButton.disabled = atcButton.disabled || atcButton.getAttribute('aria-disabled') === 'true';
    };
    sync();
    new MutationObserver(sync).observe(atcButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
    });
  }

  // internationally redirected shoppers fall back to Shopify's native dynamic checkout button
  // instead of the Flo buy-now button
  bindBuyNowIntlFallback() {
    if (!this.buyNowButton || !this.nativeBuyNowWrapper) return;
    if (!window.location.pathname.includes('products')) return;

    if (this.shouldTriggerFloDirectly()) {
      this.buyNowButton.style.setProperty('display', 'flex', 'important');
      this.nativeBuyNowWrapper.style.setProperty('display', 'none', 'important');
    } else {
      this.nativeBuyNowWrapper.style.setProperty('display', 'block', 'important');
      this.buyNowButton.style.setProperty('display', 'none', 'important');
    }
  }

  // shopflo-event:add-to-cart fires right after every successful add-to-cart (see
  // assets/cart-drawer.js / assets/cart-notification.js), regardless of which Cart behavior
  // (Theme Editor > Shopflo > Cart behavior) is configured:
  // - "Auto Open" (shopflo_cart_open_after_atc !== false): open the cart immediately.
  // - "Cart Click" (shopflo_cart_open_after_atc === false): must NOT show the cart yet, but
  //   still tell the bundle to load its content in the background so the eventual cart-icon
  //   click (see bindCartIconTriggers() / openThemeFloCart()) opens instantly instead of
  //   loading cold.
  bindDomEvents() {
    document.addEventListener('shopflo-event:add-to-cart', () => {
      if (!this.shouldTriggerFloDirectly()) {
        return;
      }
      if (typeof window.handleFloCartBtn !== 'function') {
        return;
      }
      if (this.config.shopflo_cart_open_after_atc === false) {
        window.handleFloCartBtn(null, { loadCartInBackground: true });
      } else {
        window.handleFloCartBtn();
      }
    });
  }

  // Any element marked data-js-shopflo-cart-icon (see sections/header.liquid) opens Shopflo's
  // cart on click - no custom event involved, just a direct delegated listener, so markup only
  // needs the one data attribute and no onclick/JS wiring of its own. Always a plain open (see
  // openThemeFloCart()) - the bundle's own content is already warm by now regardless of Cart
  // behavior, either from the "Auto Open" open itself or the "Cart Click" background load above.
  bindCartIconTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-js-shopflo-cart-icon]');
      if (!trigger) return;
      event.preventDefault();
      this.openThemeFloCart();
    });
  }

  // Drives the shared dummy popup (#popupOverlay/#popupWrapper, rendered once in shopflo.liquid)
  // through a button<->full-screen FLIP animation that we fully own, instead of trying to morph
  // Shopflo's own injected checkout markup (fragile: we don't control its structure or timing).
  // Buttons carry .shopflo-popup-trigger + data-flo-action="checkout|buy-now" instead of onclick,
  // so any number of Buy Now / Checkout buttons on the page can share this one popup. Only once
  // the OPEN transition finishes do we hand off to the real Shopflo checkout; closing is driven by
  // Shopflo's "FLO_EXIT_CHECKOUT" postMessage, which shrinks the popup back to its trigger button.
  bindPopupMorph() {
    // reduceMotion covers both the OS-level accessibility preference and the Theme Editor's
    // "Enable checkout popup animation" toggle. With no animation to play, the dummy popup adds
    // nothing (it would just flash open then immediately hand off) - so skip it and its whole
    // DOM/JS machinery entirely, and call Shopflo's real functions directly on click, same as
    // before the popup-morph system existed.
    this.reduceMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      this.config.shopflo_enable_popup_animation === false;

    if (this.reduceMotion) {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('.shopflo-popup-trigger');
        if (!trigger || trigger.disabled) return;
        if (trigger.dataset.floAction === 'buy-now') {
          if (typeof window.handleFloBuyNowBtn === 'function') window.handleFloBuyNowBtn(event);
        } else {
          this.openThemeFloCheckout();
        }
      });
      return;
    }

    this.popupOverlay = document.getElementById('popupOverlay');
    this.popupWrapper = document.getElementById('popupWrapper');
    if (!this.popupOverlay || !this.popupWrapper) return;

    this.popupAnimating = false;
    this.activePopupTrigger = null;
    this.pendingFloAction = null;
    this.pendingFloEvent = null;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.shopflo-popup-trigger');
      if (!trigger || trigger.disabled || this.popupAnimating) return;

      // Matches openThemeFloCheckout()'s own fallback branch (disabled entirely, or an
      // internationally-redirected shopper): a full page navigation is instant, so there's
      // nothing for the popup to usefully animate into - navigate directly instead.
      if (trigger.dataset.floAction === 'checkout' && !this.shouldTriggerFloDirectly()) {
        window.location.href = '/checkout';
        return;
      }

      this.openPopupFrom(trigger, event);
    });

    const closeBtn = document.getElementById('popupClose');
    if (closeBtn) closeBtn.addEventListener('click', () => this.closePopup());

    this.popupOverlay.addEventListener('click', (event) => {
      if (event.target === this.popupOverlay) this.closePopup();
    });

    window.addEventListener('message', (event) => this.handleFloMessage(event));
  }

  handleFloMessage(event) {
    const data = event.data;
    const type = typeof data === 'string' ? data : data && (data.type || data.event || data.name);
    if (type === 'FLO_EXIT_CHECKOUT') this.closePopup();
  }

  // ratio of the trigger button's on-screen box to the popup's fixed natural width, expressed as
  // a translate+scale so `transform-origin: 0 0` makes the popup sit exactly over the button.
  // Height is NOT scaled here - it's a real property transition (see triggerHeightFor/--h) so it
  // doesn't distort border-radius/content the way non-uniform scale(sx, sy) would.
  popupRectFor(trigger) {
    const triggerRect = trigger.getBoundingClientRect();
    const popupW = this.popupWrapper.offsetWidth;
    return {
      x: triggerRect.left,
      y: triggerRect.top,
      sx: popupW ? triggerRect.width / popupW : 1,
    };
  }

  setPopupTransformVars(rect) {
    this.popupWrapper.style.setProperty('--tx', `${rect.x}px`);
    this.popupWrapper.style.setProperty('--ty', `${rect.y}px`);
    this.popupWrapper.style.setProperty('--sx', String(rect.sx));
  }

  // real (px) height of whichever button triggered the popup
  triggerHeightFor(trigger) {
    return `${trigger.getBoundingClientRect().height}px`;
  }

  // the popup's own resting height (--popup-h, responsive per breakpoint), read as a concrete
  // value so it can be transitioned to/from
  targetPopupHeight() {
    return getComputedStyle(this.popupWrapper).getPropertyValue('--popup-h').trim() || '100%';
  }

  setPopupHeight(height) {
    this.popupWrapper.style.setProperty('--h', height);
  }

  // the popup's FLIP start/end radius - matches the trigger button's own live computed
  // border-radius exactly, so the popup starts (open) / ends (close) looking like the button
  triggerRadiusFor(trigger) {
    return getComputedStyle(trigger).borderRadius || '0px';
  }

  // the popup's own resting radius (--popup-radius, responsive per breakpoint - see
  // shopflo-css-variables.liquid), read as a concrete value so it can be transitioned to/from
  targetPopupRadius() {
    return getComputedStyle(this.popupWrapper).getPropertyValue('--popup-radius').trim() || '0px';
  }

  setPopupRadius(radius) {
    this.popupWrapper.style.setProperty('--radius', radius);
  }

  // total morph duration in ms, read from --duration so the opacity edge below always tracks it
  getDurationMs() {
    const raw = getComputedStyle(this.popupWrapper).getPropertyValue('--duration').trim();
    const ms = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
    return Number.isFinite(ms) && ms > 0 ? ms : 500;
  }

  // timing for the popup wrapper's OWN opacity edge-fade: a short ~8% slice of the overall
  // morph, positioned at the very start (edge:'start') or very end (edge:'end') of --duration.
  // 8% (~40ms on the current 550ms --duration) reads fine here because it's layered on top of
  // a transform/radius change that's already visibly moving - the eye reads the opacity shift
  // as part of that motion rather than judging it as its own fade.
  edgeFadeTiming(edge) {
    const total = this.getDurationMs();
    const edgeDuration = Math.round(total * 0.08);
    const delay = edge === 'end' ? total - edgeDuration : 0;
    return { edgeDuration, delay };
  }

  // Shared close-only crossfade window: the popup wrapper (opaque, sitting on top of the
  // button z-index-wise) and the trigger button MUST fade on the exact same duration/delay, or
  // the still-opaque wrapper simply masks however far the button has already faded in - which
  // is what made it look like the button "pops in" only after the shrink visually finishes.
  closeFadeTiming() {
    const duration = 1950;
    const delay = Math.max(0, this.getDurationMs() - duration);
    return { duration, delay };
  }

  // the opacity fade should only cover a short slice of the overall transform/radius morph on
  // open, not the whole duration - so it reads as a quick flourish rather than a slow cross-fade.
  // On close, it's synced with fadeTrigger's window instead (see closeFadeTiming).
  setPopupOpacityTiming(edge) {
    if (edge === 'end') {
      const { duration, delay } = this.closeFadeTiming();
      this.popupWrapper.style.setProperty('--opacity-duration', `${duration}ms`);
      this.popupWrapper.style.setProperty('--opacity-delay', `${delay}ms`);
      return;
    }
    const { edgeDuration, delay } = this.edgeFadeTiming(edge);
    this.popupWrapper.style.setProperty('--opacity-duration', `${edgeDuration}ms`);
    this.popupWrapper.style.setProperty('--opacity-delay', `${delay}ms`);
  }

  // hides the real trigger button within a 50ms window at the very start of the expand
  // (edge:'start', open). On close (edge:'end'), it fades in over the same window the popup
  // wrapper itself is fading out over (closeFadeTiming), so both happen as one crossfade.
  fadeTrigger(trigger, edge, targetOpacity) {
    if (edge === 'end') {
      const { duration, delay } = this.closeFadeTiming();
      trigger.style.transition = `opacity ${duration}ms linear ${delay}ms`;
      trigger.style.opacity = String(targetOpacity);
      return;
    }
    trigger.style.transition = 'opacity 50ms linear';
    trigger.style.opacity = String(targetOpacity);
  }

  openPopupFrom(trigger, originClickEvent) {
    this.popupAnimating = true;
    this.activePopupTrigger = trigger;
    this.pendingFloAction = trigger.dataset.floAction;
    this.pendingFloEvent = originClickEvent;

    this.showPopupIfHidden();
    this.popupOverlay.classList.add('is-open');
    trigger.classList.add('sf-popup-trigger--hidden');

    this.popupWrapper.style.transition = 'none';
    this.setPopupTransformVars(this.popupRectFor(trigger));
    this.setPopupRadius(this.triggerRadiusFor(trigger));
    this.setPopupHeight(this.triggerHeightFor(trigger));
    this.popupWrapper.style.opacity = '0';
    void this.popupWrapper.offsetWidth; // force layout so the "start" position paints before we animate away from it

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Measure the natural resting height/width while transitions are STILL disabled (from
        // the instant-jump phase above), so offsetHeight reports the real target height rather
        // than a transition's in-progress (starting) value.
        this.setPopupHeight(this.targetPopupHeight());
        const popupW = this.popupWrapper.offsetWidth;
        const popupH = this.popupWrapper.offsetHeight;

        // Restore --h to the trigger's height and FORCE A FLUSH here (still under
        // transition:none) - without this, the browser's next transition comparison would use
        // the measurement snapshot above (already at the target) as its baseline, see no net
        // change once --h is set to the target again below, and silently skip animating height
        // entirely (it would just snap to full size on frame one).
        this.setPopupHeight(this.triggerHeightFor(trigger));
        void this.popupWrapper.offsetHeight;

        this.popupWrapper.style.transition = '';
        this.popupOverlay.classList.add('active');

        this.setPopupTransformVars({
          x: (window.innerWidth - popupW) / 2,
          y: (window.innerHeight - popupH) / 2,
          sx: 1,
        });
        this.setPopupRadius(this.targetPopupRadius());
        this.setPopupHeight(this.targetPopupHeight());
        this.setPopupOpacityTiming('start');
        this.popupWrapper.style.opacity = '1';
        this.fadeTrigger(trigger, 'start', 0);

        // local flag (not instance state) so the transitionend/timeout race can only ever
        // run the hand-off once per cycle, regardless of what a later cycle does to `this`
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          this.popupAnimating = false;
          this.runPendingFloAction();
        };
        this.popupWrapper.addEventListener(
          'transitionend',
          (e) => {
            if (e.propertyName !== 'transform') return;
            finish();
          },
          { once: true }
        );
        // safety net in case the transition is interrupted and transitionend never fires -
        // scaled to --duration (plus a buffer) so it never fires before the real transition
        // could possibly have finished, however long --duration is set to
        setTimeout(finish, this.getDurationMs() + 150);
      });
    });
  }

  runPendingFloAction() {
    const action = this.pendingFloAction;
    const originEvent = this.pendingFloEvent;
    this.pendingFloAction = null;
    this.pendingFloEvent = null;

    if (action === 'buy-now') {
      if (typeof window.handleFloBuyNowBtn === 'function') window.handleFloBuyNowBtn(originEvent);
    } else {
      this.openThemeFloCheckout();
    }

    // Shopflo's own checkout/cart overlay mounts at an extreme z-index (see its injected
    // stylesheet) so it already paints over our popup - this hide is just cleanup, giving the
    // handoff call a tick to fire before we get out of the way. Kept as `visibility` (not
    // display/is-open) so closePopup() can reveal it again for the shrink-back animation
    // without disturbing its transform/transition state.
    setTimeout(() => {
      this.popupOverlay.style.visibility = 'hidden';
    }, 10);
  }

  showPopupIfHidden() {
    if (this.popupOverlay.style.visibility === 'hidden') {
      this.popupOverlay.style.visibility = '';
    }
  }

  closePopup() {
    const trigger = this.activePopupTrigger;
    if (!trigger || this.popupAnimating) return;
    this.popupAnimating = true;

    this.showPopupIfHidden();
    this.popupOverlay.classList.remove('active');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      this.popupOverlay.classList.remove('is-open');
      trigger.classList.remove('sf-popup-trigger--hidden');
      // release inline control back to the button's own CSS-driven transition now that the
      // crossfade is done (or was skipped) - it's already visually at full opacity by now
      trigger.style.opacity = '';
      trigger.style.transition = '';
      this.activePopupTrigger = null;
      this.popupAnimating = false;
    };

    if (!document.body.contains(trigger)) {
      this.popupWrapper.style.opacity = '0';
      finish();
      return;
    }

    this.setPopupTransformVars(this.popupRectFor(trigger));
    this.setPopupRadius(this.triggerRadiusFor(trigger));
    this.setPopupHeight(this.triggerHeightFor(trigger));
    this.setPopupOpacityTiming('end');
    this.popupWrapper.style.opacity = '0';
    this.fadeTrigger(trigger, 'end', 1);
    this.popupWrapper.addEventListener(
      'transitionend',
      (e) => {
        if (e.propertyName !== 'transform') return;
        finish();
      },
      { once: true }
    );
    // safety net: a transition can be silently aborted (no transitionend fired) - scaled to
    // --duration (plus a buffer) so it never fires before the real transition could possibly
    // have finished, however long --duration is set to
    setTimeout(finish, this.getDurationMs() + 150);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.shopfloThemeInstance = new ShopfloTheme();
});

window.ShopfloTheme = ShopfloTheme;

/**
 * Shopflo Accounts (shop_pass)
 * ----------------------------------------------------------------
 * Defines <shopflo-accounts>, a custom element that bridges fully
 * customizable, theme-authored "proxy" markup (rendered via
 * {% render 'shopflo', type: 'shop_pass' %}, see snippets/shopflo.liquid)
 * to the hidden "actual" markup that the separate Shopflo bundle script
 * controls (handleDrawer, handleShopifyLogin, and its own id-targeted
 * logout wiring).
 *
 * Design summary:
 *  - Everything is static at parse time. No runtime DOM injection,
 *    no MutationObserver, no selector-based element replacement.
 *  - The hidden "actual" markup exists exactly once per page
 *    (rendered in layout/theme.liquid) and is never duplicated per
 *    instance, since the bundle targets it by id.
 *  - Any number of <shopflo-accounts> proxy instances can exist on
 *    a page (desktop header, mobile menu, footer, ...). Each is
 *    independent for its own local triggers/state, but all
 *    instances stay in sync for global truths like login state via
 *    a broadcast event.
 *  - Proxy markup declares its role via data-flo-trigger /
 *    data-flo-state attributes. This file never hardcodes ids, so
 *    theme authors can freely add/remove/restructure proxy markup
 *    without touching this file.
 *  - Only the two inline-onclick-dependent bindings (drawer toggle,
 *    account login) need to wait for the bundle's global functions
 *    to exist. Logout needs no such wait — the bundle wires it via
 *    getElementById, independent of any global function.
 */

/**
 * Global config. Edit this object to change behavior across every
 * <shopflo-accounts> instance on the page — instance-level
 * differences (labels, initial state, markup) belong in the
 * .liquid snippet / theme settings, not here.
 */
const ShopfloAccountsConfig = {
  // Bindings are keyed to the hidden "actual" markup's ids, which
  // are contracted to the external Shopflo bundle script.
  //
  // The header icon itself is handled separately, NOT through this
  // list — its correct forwarding target (#flo-login-button-header-logo
  // vs #icon-shopflo) depends on which one is actually visible right
  // now, resolved fresh on every click, not on a fixed mapping. See
  // _resolveAuthState / _handleHeaderIconClick below.
  bindings: [
    {
      // Drawer's "Account" menu item — navigates to the account page,
      // which also causes the bundle to append a real login iframe
      // (+ overlay) as a sibling inside the hidden markup. onForwarded
      // reacts to that once the click has been forwarded.
      actualId: 'account-button-desktop',
      requiredGlobalFns: ['handleShopifyLogin'],
      triggerAttr: 'account-login',
      onForwarded: (instance) => instance._handleAccountIframeFlow(),
    },
    {
      // Logs the customer out. The bundle wires this by id, not by
      // a global function, so there is nothing to wait on here.
      actualId: 'logout-button-desktop',
      requiredGlobalFns: [],
      triggerAttr: 'account-logout',
      optimisticEffect: {
        hide: ['drawer', 'account-icon'],
        show: ['login-icon'],
      },
      // Logout is a real, page-wide truth — every instance on the
      // page should reflect it immediately, not just the one that
      // was clicked.
      broadcast: true,
    },
    {
      // Our overlay's close click forwards here — icon-shopflo is
      // what the bundle itself uses to tear down its real iframe, so
      // reuse that same real teardown rather than inventing our own.
      // onForwarded force-closes everything on our side too (drawer,
      // iframe, overlay itself) — not a toggle, since the overlay is
      // only ever shown while something is open, so a click on it
      // always means "close", regardless of which view was showing.
      actualId: 'icon-shopflo',
      requiredGlobalFns: ['handleDrawer'],
      triggerAttr: 'close-iframe',
      onForwarded: (instance) => instance._handleOverlayClose(),
    },
  ],

  // Bounded readiness poll for the bundle-defined globals only.
  // Static markup means there is nothing to wait on for
  // placement — only for the separate bundle script to finish
  // evaluating and defining window.handleDrawer / window.handleShopifyLogin.
  ready: {
    intervalMs: 150,
    maxAttempts: 20, // ~3s ceiling
    onTimeout: 'warn', // 'warn' | 'silent'
  },

  // The two hidden elements that reveal Shopflo's real, current auth
  // state — whichever is actually visible is the truth, checked via
  // getComputedStyle rather than assumed.
  authStateIds: {
    loggedIn: 'loggedin-icons',
    loggedOut: 'flo-login-button-header-logo',
  },

  // The bundle appends its OWN real iframe on account-login click. We
  // The bundle appends its OWN real iframe on account-login click.
  // DESKTOP: we don't reveal/reposition it — we copy its src into our
  // own iframe (in normal, always-visible proxy markup) instead,
  // since the real one sits inside our off-screen hidden wrapper and
  // can't be shown directly there.
  // MOBILE: the bundle uses a different, self-contained sheet+overlay
  // component that we leave fully visible and untouched — no copying,
  // since click forwarding (open and close) already works correctly
  // against it directly, and this avoids any risk of postMessage-based
  // completion signals failing against a duplicate iframe.
  iframe: {
    sourceId: 'flo-shopify-login-iframe',
    mobileBreakpoint: 699,
  },

  debug: false,
};

const SHOPFLO_ACCOUNTS_BROADCAST_EVENT = 'shopflo-accounts:optimistic-effect';

class ShopfloAccounts extends HTMLElement {
  connectedCallback() {
    // Guards against connectedCallback firing more than once for the
    // same element (e.g. if it's ever disconnected/reconnected by
    // the theme — a sticky-header clone, a section re-render, etc.).
    // Without this, a second connectedCallback would attach a SECOND
    // click listener to the same button, so one physical click could
    // fire _handleHeaderIconClick twice in the same tick — and if
    // real auth state changes between those two firings, each one
    // can resolve a different branch, producing mixed behavior.
    if (this._shopfloInitialized) return;
    this._shopfloInitialized = true;

    this._onBroadcast = this._onBroadcast.bind(this);
    document.addEventListener(SHOPFLO_ACCOUNTS_BROADCAST_EVENT, this._onBroadcast);

    this._onPageShow = this._onPageShow.bind(this);
    window.addEventListener('pageshow', this._onPageShow);

    // Resolve real state once on load and reflect it immediately —
    // no waiting/polling needed, this is a synchronous style read.
    this._applyAuthState(this._resolveAuthState());

    this._setupHeaderIconTrigger();
    ShopfloAccountsConfig.bindings.forEach((binding) => this._setupBinding(binding));
  }

  disconnectedCallback() {
    document.removeEventListener(SHOPFLO_ACCOUNTS_BROADCAST_EVENT, this._onBroadcast);
    window.removeEventListener('pageshow', this._onPageShow);
  }

  /**
   * Fires on every page show, including when the page is restored
   * from the browser's back-forward cache (bfcache) after the user
   * navigates away — e.g. a link inside the iframe redirecting the
   * whole page, then pressing back. A bfcache restore does NOT re-run
   * connectedCallback's normal setup, since the page isn't reloaded,
   * just resumed exactly as it was — so whatever was open when they
   * left (drawer or iframe) is still open when they return. This
   * explicitly resets that. event.persisted is what distinguishes a
   * genuine bfcache restore from an ordinary load, where this should
   * do nothing (normal setup already handles the closed default).
   */
  _onPageShow(event) {
    if (!event.persisted) return;

    const wasOpen = this.querySelector(
      '[data-flo-state="drawer"][data-flo-visible="true"], [data-flo-state="drawer-iframe"][data-flo-visible="true"]'
    );
    if (!wasOpen) return; // nothing left open — do nothing, so we never risk toggling the real drawer OPEN via a needless forwarded click

    // Reuses the exact same real-forwarding + full reset flow as a
    // genuine overlay click, rather than duplicating that logic here.
    const closeTrigger = this.querySelector('[data-flo-trigger="close-iframe"]');
    if (closeTrigger) {
      closeTrigger.click();
    } else {
      this._handleOverlayClose();
    }
  }

  _globalsReady(fnNames) {
    return fnNames.every((name) => typeof window[name] === 'function');
  }

  /**
   * Resolves once required global functions exist, or once
   * maxAttempts is exhausted (resolves false in that case).
   * Resolves immediately (true) if no globals are required.
   */
  _waitForGlobals(fnNames) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      if (!fnNames.length || this._globalsReady(fnNames)) {
        resolve(true);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (this._globalsReady(fnNames)) {
          clearInterval(timer);
          resolve(true);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(false);
        }
      }, readyConfig.intervalMs);
    });
  }

  /**
   * Resolves with the element once it exists in the DOM, or null once
   * maxAttempts is exhausted. Used to detect the bundle's own
   * dynamically-appended iframe without a MutationObserver — this is
   * a short, bounded poll only, not a continuous watch.
   */
  _waitForElement(id) {
    const readyConfig = ShopfloAccountsConfig.ready;
    return new Promise((resolve) => {
      const existing = document.getElementById(id);
      if (existing) {
        resolve(existing);
        return;
      }
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        const el = document.getElementById(id);
        if (el) {
          clearInterval(timer);
          resolve(el);
        } else if (attempts >= readyConfig.maxAttempts) {
          clearInterval(timer);
          resolve(null);
        }
      }, readyConfig.intervalMs);
    });
  }

  /**
   * Checks which of the two hidden actual elements is really visible
   * right now and returns 'logged-in' or 'logged-out' accordingly.
   * Uses getComputedStyle rather than reading inline style directly,
   * since whatever sets these could use either a class or an inline
   * style — computed style reflects the outcome regardless of how
   * it was set.
   */
  _resolveAuthState() {
    const { loggedIn, loggedOut } = ShopfloAccountsConfig.authStateIds;
    const loggedInEl = document.getElementById(loggedIn);
    const loggedOutEl = document.getElementById(loggedOut);

    const loggedInVisible = !!loggedInEl && getComputedStyle(loggedInEl).display !== 'none';
    const loggedOutVisible = !!loggedOutEl && getComputedStyle(loggedOutEl).display !== 'none';

    if (loggedInVisible) return 'logged-in';
    if (loggedOutVisible) return 'logged-out';

    // Neither resolvable (elements missing, or bundle hasn't set
    // either yet) — default to logged-out as the safer assumption.
    console.warn('[shopflo-accounts]', 'Could not resolve real auth state from #' + loggedIn + ' / #' + loggedOut + ' — defaulting to logged-out.');
    return 'logged-out';
  }

  /** Reflects a resolved auth state onto this instance's own proxy elements. */
  _applyAuthState(state) {
    const isLoggedIn = state === 'logged-in';
    this.querySelectorAll('[data-flo-state="login-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'false' : 'true');
    });
    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-visible', isLoggedIn ? 'true' : 'false');
    });
  }

  _setupHeaderIconTrigger() {
    const triggers = this.querySelectorAll('[data-flo-trigger="header-icon"]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => this._handleHeaderIconClick(event));
    });
  }

  _handleHeaderIconClick(event) {
    // Re-check right before forwarding, rather than trusting whatever
    // this instance last rendered — state may have changed since load.
    const state = this._resolveAuthState();
    this._applyAuthState(state);

    if (state === 'logged-in') {
      if (typeof window.handleDrawer !== 'function') {
        console.warn('[shopflo-accounts]', 'handleDrawer not yet available — ignoring click.');
        return;
      }
      const actual = document.getElementById('icon-shopflo');
      if (!actual) {
        console.warn('[shopflo-accounts]', 'Actual element #icon-shopflo not found — is shopflo-accounts-hidden rendered on this page?');
        return;
      }
      actual.click();
      this._closeIframeFlow();
      const opening = this._isDrawerClosed(); // check BEFORE toggling
      if (opening) {
        this._positionDrawer();
      }
      this._toggleState('drawer', event.currentTarget);
      this._setOverlayVisible(opening);
    } else {
      const actual = document.getElementById('flo-login-button-header-logo');
      if (!actual) {
        console.warn('[shopflo-accounts]', 'Actual element #flo-login-button-header-logo not found — is shopflo-accounts-hidden rendered on this page?');
        return;
      }
      actual.click();
    }
  }
  _setupBinding(binding) {
    // Scoped to THIS instance only — every <shopflo-accounts> on
    // the page discovers and binds its own local triggers
    // independently, however many exist, wherever they're nested.
    const triggers = this.querySelectorAll('[data-flo-trigger="' + binding.triggerAttr + '"]');
    if (!triggers.length) return; // this instance doesn't use this binding — not an error

    this._waitForGlobals(binding.requiredGlobalFns).then((ok) => {
      if (!ok) {
        if (ShopfloAccountsConfig.ready.onTimeout === 'warn') {
          console.warn(
            '[shopflo-accounts]',
            'Timed out waiting for global function(s) [' + binding.requiredGlobalFns.join(', ') + ']' +
            ' required by trigger "' + binding.triggerAttr + '". Leaving control(s) disabled rather than' +
            ' enabling a button that would silently do nothing.'
          );
        }
        return;
      }

      triggers.forEach((trigger) => {
        trigger.disabled = false;
        trigger.addEventListener('click', (event) => this._handleTriggerClick(event, binding));
      });

      if (ShopfloAccountsConfig.debug) {
      }
    });
  }

  _handleTriggerClick(event, binding) {
    // The actual element is shared/singular across the whole page,
    // so it is always looked up on document, never scoped to this
    // instance.
    const actual = document.getElementById(binding.actualId);
    if (!actual) {
      console.warn('[shopflo-accounts]', 'Actual element #' + binding.actualId + ' not found — is the hidden markup rendered on this page?');
      return;
    }

    // Forward the click to the real, bundle-wired element so
    // whatever it actually does (navigate, toggle its own
    // internal state, etc.) still happens correctly.
    actual.click();

    if (binding.toggleState) {
      this._toggleState(binding.toggleState, event.currentTarget);
    }

    if (binding.optimisticEffect) {
      if (binding.broadcast) {
        document.dispatchEvent(
          new CustomEvent(SHOPFLO_ACCOUNTS_BROADCAST_EVENT, { detail: { effect: binding.optimisticEffect } })
        );
      } else {
        this._applyOptimisticEffect(binding.optimisticEffect);
      }
    }

    if (binding.onForwarded) {
      binding.onForwarded(this);
    }
  }

  _onBroadcast(event) {
    this._applyOptimisticEffect(event.detail.effect);
  }

  // Bidirectional toggle for this instance's own local state
  // (e.g. opening/closing its own drawer). Not broadcast — each
  // instance's drawer open/closed state is local UI, not a
  // page-wide truth like login state.
  _toggleState(state, triggerEl) {
    const targets = this.querySelectorAll('[data-flo-state="' + state + '"]');
    targets.forEach((el) => {
      const isVisible = el.getAttribute('data-flo-visible') === 'true';
      el.setAttribute('data-flo-visible', isVisible ? 'false' : 'true');
    });
    if (triggerEl) {
      const expanded = triggerEl.getAttribute('aria-expanded') === 'true';
      triggerEl.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
  }

  _isDrawerClosed() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    return !drawerEl || drawerEl.getAttribute('data-flo-visible') !== 'true';
  }

  /**
   * Flips the drawer's default open-below/open-right anchor to
   * open-above and/or open-left whenever the trigger is too close to
   * that edge of the viewport for the drawer's own (effectively
   * fixed) size to fit. Runs right before the drawer is revealed, so
   * the flip is already applied on the very first paint - no
   * flash of the wrong position.
   *
   * The decision is resolved once per variant (desktop/mobile) per
   * browser session and cached in sessionStorage, rather than
   * re-measuring on every open - the trigger's position relative to
   * the viewport it lives in doesn't change between clicks within the
   * same session, so re-measuring every time is pure waste.
   */
  _positionDrawer() {
    const drawerEl = this.querySelector('[data-flo-state="drawer"]');
    const anchorEl = this.querySelector('[data-flo-state="account-icon"]');
    if (!drawerEl || !anchorEl) return;

    const cacheKey = 'shopflo_account_drawer_position_' + (this.classList.contains('shopflo-accounts--mob') ? 'mob' : 'desk');
    let position = this._readCachedDrawerPosition(cacheKey);
    if (!position) {
      position = this._computeDrawerPosition(drawerEl, anchorEl);
      this._writeCachedDrawerPosition(cacheKey, position);
    }
    this._applyDrawerPosition(drawerEl, position);
  }

  _readCachedDrawerPosition(cacheKey) {
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.vertical === 'above' || parsed.vertical === 'below') && (parsed.horizontal === 'left' || parsed.horizontal === 'right')) {
        return parsed;
      }
      return null;
    } catch (e) {
      return null; // storage unavailable (e.g. private browsing) - fall through to computing fresh every time
    }
  }

  _writeCachedDrawerPosition(cacheKey, position) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(position));
    } catch (e) {
      // storage unavailable - nothing to do, next click just recomputes
    }
  }

  /**
   * Measures the drawer's real rendered size while it's still closed,
   * by temporarily forcing it visible-but-invisible (inline
   * `!important` beats the stylesheet's `[data-flo-visible="false"]
   * { display: none !important }` rule - see setPopupHeight/etc. in
   * ShopfloTheme for the same trick). No flash: visibility:hidden
   * never paints anything, and both overrides are removed again
   * before this function returns.
   */
  _measureDrawerSize(drawerEl) {
    drawerEl.style.setProperty('display', 'block', 'important');
    drawerEl.style.setProperty('visibility', 'hidden', 'important');
    const rect = drawerEl.getBoundingClientRect();
    drawerEl.style.removeProperty('display');
    drawerEl.style.removeProperty('visibility');
    return { width: rect.width, height: rect.height };
  }

  _computeDrawerPosition(drawerEl, anchorEl) {
    const anchorRect = anchorEl.getBoundingClientRect();
    const { width, height } = this._measureDrawerSize(drawerEl);
    const viewportW = document.documentElement.clientWidth;
    const viewportH = document.documentElement.clientHeight;

    // Default is open-below (top:100%): fits if there's enough room
    // beneath the anchor; otherwise flip to open-above.
    const spaceBelow = viewportH - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const vertical = (spaceBelow >= height || spaceBelow >= spaceAbove) ? 'below' : 'above';

    // Default is open-right (right:0, extending toward the left):
    // fits if there's enough room to the left of the anchor's right
    // edge; otherwise flip to open-left (extending toward the right).
    const spaceForRightAlign = anchorRect.right;
    const spaceForLeftAlign = viewportW - anchorRect.left;
    const horizontal = (spaceForRightAlign >= width || spaceForRightAlign >= spaceForLeftAlign) ? 'right' : 'left';

    return { vertical, horizontal };
  }

  _applyDrawerPosition(drawerEl, position) {
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-top', position.vertical === 'above');
    drawerEl.classList.toggle('shopflo-accounts__drawer--open-left', position.horizontal === 'left');
  }

  _setOverlayVisible(visible) {
    this.querySelectorAll('[data-flo-state="account-iframe-overlay"]').forEach((el) => {
      el.setAttribute('data-flo-visible', visible ? 'true' : 'false');
    });
  }

  /**
   * The overlay is only ever shown while the drawer or iframe is
   * open, so a click on it always means "close everything" — this is
   * a force-close, not a toggle. Also forwarded to the real
   * icon-shopflo (see the close-iframe binding), so real state stays
   * in sync with what the user sees here.
   */
  _handleOverlayClose() {
    this._closeIframeFlow();
    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });
    this._setOverlayVisible(false);
    this.querySelectorAll('[data-flo-trigger="header-icon"]').forEach((el) => {
      if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'false');
    });
  }

  // One-directional show/hide, used for logout's effect. Applied
  // within this instance only — when broadcast, every instance on
  // the page runs this independently in response to the same event.
  _applyOptimisticEffect(effect) {
    (effect.hide || []).forEach((state) => {
      this.querySelectorAll('[data-flo-state="' + state + '"]').forEach((el) => {
        el.setAttribute('data-flo-visible', 'false');
      });
    });
    (effect.show || []).forEach((state) => {
      this.querySelectorAll('[data-flo-state="' + state + '"]').forEach((el) => {
        el.setAttribute('data-flo-visible', 'true');
      });
    });
  }

  /**
   * Called after the "Account" drawer item's click has been forwarded.
   * Waits for the bundle to append its iframe, then opens it.
   */
  _handleAccountIframeFlow() {
    this._waitForElement(ShopfloAccountsConfig.iframe.sourceId).then((iframeEl) => {
      if (!iframeEl) {
        console.warn(
          '[shopflo-accounts]',
          'Timed out waiting for #' + ShopfloAccountsConfig.iframe.sourceId + ' to appear after account-login click.'
        );
        return;
      }
      this._openIframeFlow(iframeEl);
    });
  }

  /**
   * Desktop: copies the bundle's real iframe src into OUR OWN iframe
   * (a sibling of the drawer, matching the real DOM's own structure)
   * and swaps from the plain menu drawer to the iframe view. Does NOT
   * touch the real iframe or the hidden wrapper at all — our copy
   * lives in normal visible proxy markup, styled via CSS as usual.
   *
   * Mobile: does NOT copy anything — the bundle's own real sheet
   * (flo-shopify-login-sheet-overlay / -sheet / -iframe) is left
   * fully visible and untouched. We only hide our own simple menu
   * drawer and overlay, then step out of the way entirely.
   */
  _openIframeFlow(realIframeEl) {
    this.querySelectorAll('[data-flo-state="drawer"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });

    const isMobile = window.matchMedia('(max-width: ' + ShopfloAccountsConfig.iframe.mobileBreakpoint + 'px)').matches;
    if (isMobile) {
      // The bundle's real sheet+overlay renders itself — nothing more
      // for us to do. Hide our own overlay too, since the real one
      // already covers that role; showing both would double-dim.
      this.querySelectorAll('[data-flo-state="account-iframe-overlay"]').forEach((el) => {
        el.setAttribute('data-flo-visible', 'false');
      });
      return;
    }

    const src = realIframeEl.getAttribute('src');
    if (!src) {
      console.warn('[shopflo-accounts]', 'Real iframe #' + ShopfloAccountsConfig.iframe.sourceId + ' has no src yet.');
      return;
    }

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('src', src);
      iframeEl.setAttribute('data-flo-visible', 'true');
    });

    this.querySelectorAll('[data-flo-state="account-iframe-overlay"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'true');
    });

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.setAttribute('data-flo-iframe-active', 'true');
    });

    this._startIframeSizeSync(realIframeEl);
  }

  /**
   * Keeps our copy's size matched to the real content's, which
   * changes at times we don't control (the user interacting inside
   * it). Scoped narrowly — one element, one attribute, only for as
   * long as the iframe view is actually open — and disconnected in
   * _closeIframeFlow, so cost is effectively zero outside that window.
   */
  _startIframeSizeSync(realIframeEl) {
    const applySize = () => {
      // Prefer the literal inline height value (the TARGET the bundle
      // set) over getComputedStyle. If the real iframe animates its
      // height via a CSS transition, getComputedStyle can report a
      // mid-transition frame instead of the destination value. The
      // inline string is immune to that, since it's the target, not
      // the current render. Only fall back to computed style if
      // there's no inline value to read (e.g. height set via a class).
      // Confirmed on both desktop and mobile the iframe itself always
      // carries the correct height (matches its sheet wrapper on
      // mobile), so there's no need to branch by viewport or watch
      // more than this one element.
      let height = realIframeEl.style.height;
      if (!height) {
        height = getComputedStyle(realIframeEl).height;
      }

      // Known bad transient value — corrected every time it occurs,
      // not just on a first read, since it can recur on later
      // mutations too. Rounded comparison catches near-232 fractional
      // reads (e.g. 231.992px). Keep the 'px' unit — a unitless
      // number here would make the CSS custom property invalid.
      if (Math.round(parseFloat(height)) === 232) {
        height = '225px';
      }

      this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
        iframeEl.style.setProperty('--flo-sso-iframe-height', height);
      });
    };

    applySize();
    this._iframeSizeObserver = new MutationObserver(applySize);
    this._iframeSizeObserver.observe(realIframeEl, { attributes: true, attributeFilter: ['style'] });

    // Safety net: if height changes via a transition rather than a
    // plain inline jump, re-sync once more once it's actually settled.
    this._iframeSizeRealEl = realIframeEl;
    this._iframeTransitionEndHandler = (event) => {
      if (event.propertyName === 'height') applySize();
    };
    realIframeEl.addEventListener('transitionend', this._iframeTransitionEndHandler);
  }

  _stopIframeSizeSync() {
    if (this._iframeSizeObserver) {
      this._iframeSizeObserver.disconnect();
      this._iframeSizeObserver = null;
    }
    if (this._iframeSizeRealEl && this._iframeTransitionEndHandler) {
      this._iframeSizeRealEl.removeEventListener('transitionend', this._iframeTransitionEndHandler);
    }
    this._iframeSizeRealEl = null;
    this._iframeTransitionEndHandler = null;
  }

  /**
   * Reverses _openIframeFlow: hides our iframe/overlay, resets our
   * iframe back to about:blank (mirrors the real one being removed
   * from the DOM — stops it rather than leaving it loaded in the
   * background). Fully collapses rather than restoring the plain
   * menu — matches standard dropdown-toggle behavior. Revisit if you'd
   * rather it return to the Account/Log out menu instead.
   */
  _closeIframeFlow() {
    this._stopIframeSizeSync();

    this.querySelectorAll('[data-flo-state="drawer-iframe"]').forEach((iframeEl) => {
      iframeEl.setAttribute('data-flo-visible', 'false');
      iframeEl.setAttribute('src', 'about:blank');
      iframeEl.style.removeProperty('--flo-sso-iframe-height');
    });

    this.querySelectorAll('[data-flo-state="account-iframe-overlay"]').forEach((el) => {
      el.setAttribute('data-flo-visible', 'false');
    });

    this.querySelectorAll('[data-flo-state="account-icon"]').forEach((el) => {
      el.removeAttribute('data-flo-iframe-active');
    });
  }
}

if (!customElements.get('shopflo-accounts')) {
  customElements.define('shopflo-accounts', ShopfloAccounts);
}
